import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Check if a JWT token is expired client-side.
 */
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

// Request interceptor - attach auth token and AbortController
// Auth is primarily handled via httpOnly cookies (withCredentials: true).
// The Authorization header is set as a fallback for API clients that cannot use cookies.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('rostafy_token');
    if (token) {
      if (isTokenExpired(token)) {
        localStorage.removeItem('rostafy_token');
        window.location.href = '/login';
        return Promise.reject(new axios.Cancel('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add AbortController signal if not already provided
    if (!config.signal) {
      const controller = new AbortController();
      config.signal = controller.signal;
      config._abortController = controller;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401s globally
let isRedirecting = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !isRedirecting) {
      isRedirecting = true;
      localStorage.removeItem('rostafy_token');
      // Avoid redirect loop if already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      // Reset flag after a short delay to allow redirect
      setTimeout(() => { isRedirecting = false; }, 2000);
    }
    return Promise.reject(error);
  }
);

/**
 * Helper to create a cancellable request.
 * Usage:
 *   const { promise, cancel } = api.cancellable(api.get('/endpoint'));
 *   // Later: cancel();
 */
api.cancellable = (_requestFn) => {
  const controller = new AbortController();
  return {
    signal: controller.signal,
    cancel: () => controller.abort(),
  };
};

export default api;
